import * as React from "react";
import { FormProvider } from "react-hook-form";

import { ResourceFormActions } from "@/shared/components";
import {
  submitError,
  type SubmitError,
} from "@/shared/components/ResourceForm";
import {
  type AgentRecord,
  type CreateAgentRequest,
} from "@/shared/domain/agents";

import {
  buildRequestTemplate,
  buildVariables,
} from "../Agents/buildRequestTemplate";

import styles from "./AgentForm.module.css";
import { type AgentFormOutput } from "./agentFormSchema";
import { GoalsSection } from "./sections/GoalsSection";
import { ModelSection } from "./sections/ModelSection";
import { NameSection } from "./sections/NameSection";
import { SkillsSection } from "./sections/SkillsSection";
import { useAgentEligibleSkills } from "./skills/useAgentEligibleSkills";
import { useAgentForm } from "./useAgentForm";

export type AgentFormProps = (
  | { mode: "create" }
  /** Seeded from the stored agent. Unlike a trigger's, its name stays editable. */
  | { mode: "edit"; initial: AgentRecord }
) & {
  /**
   * Receives the built request and resolves when the page's mutation settles.
   * The pending promise is the submitting state and a rejection becomes the
   * form's own server error, so neither is a separate prop.
   *
   * Create and edit send the same four fields; the page picks POST or PUT.
   */
  onSubmit: (request: CreateAgentRequest) => Promise<void>;
  onCancel?: () => void;
};

/** The wire payload, built by the same pair the old editor sent through. */
function buildAgentRequest(values: AgentFormOutput): CreateAgentRequest {
  const form = values;
  const description = values.description.trim();
  return {
    name: values.name,
    description: description === "" ? null : description,
    request_template: buildRequestTemplate(form),
    variables: buildVariables(form),
  };
}

/**
 * The whole create-or-edit agent form: one react-hook-form form over one
 * schema, laid out as the page a person reads top to bottom.
 *
 * Every section below reads and writes the form itself through
 * `useFormContext`, so nothing here hands values down. There is no read-only
 * mode — a page that only shows an agent is not this component.
 */
export function AgentForm(props: AgentFormProps) {
  const { onSubmit, onCancel } = props;
  // Which skills the org offers is the one rule the schema cannot state on
  // its own, so it is fetched here and handed to the resolver. The Skills
  // section reads the same query for its rows and its picker.
  const { ids: eligibleSkillIds } = useAgentEligibleSkills();
  const form = useAgentForm(
    props.mode === "edit"
      ? { initial: props.initial, eligibleSkillIds }
      : { eligibleSkillIds },
  );

  // `handleSubmit` re-throws whatever the handler threw, so the page's refusal
  // is caught around the call rather than inside the handler.
  const submit = form.handleSubmit(async (values) => {
    await onSubmit(buildAgentRequest(values));
  });

  // The refusal is the page's answer, not a field's, and it is held here
  // rather than under the form's `root` error: any change after the first
  // submit re-validates the whole form, and that replaces every error the
  // schema did not produce.
  const [serverError, setServerError] = React.useState<SubmitError | null>(
    null,
  );

  return (
    <FormProvider {...form}>
      <form
        className={styles.form}
        data-testid="agent-form"
        onSubmit={(event) => {
          // Every attempt starts without the last one's refusal, so a stale
          // message never sits beside a form the person has since fixed.
          setServerError(null);
          submit(event).catch((error: unknown) => {
            setServerError(submitError(error, props.mode, "agent"));
          });
        }}
        noValidate
      >
        {props.mode === "edit" && <NameSection />}
        <GoalsSection />
        <SkillsSection />
        <ModelSection />
        {props.mode === "create" && <NameSection />}
        <ResourceFormActions
          submitLabel={
            props.mode === "create" ? "Create agent" : "Save changes"
          }
          idleHint={
            props.mode === "create"
              ? "Create the agent when it says what you want done."
              : "Save when the agent says what you want done."
          }
          submitKind={props.mode === "create" ? "create" : "save"}
          onCancel={onCancel}
          serverError={serverError}
        />
      </form>
    </FormProvider>
  );
}
